import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { toEventObject } from './helpers';
import { KeyCode } from './constants';
import { preventDefault, stopPropagation } from '~/__modules__/browser';

const SelectContext = createContext();

Select.propTypes = {
  small: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.node,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  initialOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.node,
    }),
  ),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};
export default function Select({
  small = false,
  className,
  placeholder = '',
  disabled = false,
  name,
  value,
  onChange,
  onClick = _.noop,
  onClose = _.noop,
  initialOptions = [],
  children,
}) {
  const [opened, setOpened] = useState(false);
  const handleClick = () => {
    onClick();
    setOpened((opened) => !opened);
    onClose();
  };
  const elemRef = useRef();
  useEffect(() => {
    if (!opened) return;

    const onClickBody = ({ target }) => {
      if (elemRef.current.contains(target)) return;
      setOpened(false);
      onClose();
    };
    document.body.addEventListener('click', onClickBody);
    return () => {
      document.body.removeEventListener('click', onClickBody);
    };
  }, [opened]);

  const [label, setLabel] = useState();
  useEffect(() => {
    if (value === undefined) setLabel(placeholder);
  }, [value]);

  const handleSelect = (value) => {
    setOpened(false);
    onChange(toEventObject(name, value));
    onClose();
  };

  const [options, setOptions] = useState(initialOptions);

  const contextElemRef = useRef();
  const [focusedValue, setFocusedValue] = useState();
  const prevFocusedValueRef = useRef();
  useEffect(() => {
    prevFocusedValueRef.current = focusedValue;
  }, [focusedValue]);
  useEffect(() => {
    if (!opened) return;

    const listElem = contextElemRef.current.querySelector('ul');
    const itemElems = listElem.querySelectorAll('li');
    const itemsCount = itemElems.length;
    const onKeydown = (event) => {
      const { keyCode } = event;
      if (!Object.values(KeyCode).includes(keyCode)) return;

      const index = [...listElem.children].indexOf(
        listElem.querySelector('li.focus'),
      );
      const {
        top: listElemTop,
        bottom: listElemBottom,
      } = listElem.getBoundingClientRect();

      event.preventDefault();

      switch (keyCode) {
        case KeyCode.DOWN: {
          return setFocusedValue(() => {
            const nextIndex = Math.min(index + 1, itemsCount - 1);
            const nextElem = itemElems[nextIndex];
            const nextElemBottom = nextElem.getBoundingClientRect().bottom;

            if (nextElemBottom > listElemBottom) {
              listElem.scrollTop += nextElemBottom - listElemBottom;
            }

            return options[nextIndex].value;
          });
        }
        case KeyCode.UP: {
          return setFocusedValue(() => {
            const previousIndex = Math.max(index - 1, 0);
            const previousElem = itemElems[previousIndex];
            const previousElemTop = previousElem.getBoundingClientRect().top;

            if (previousElemTop < listElemTop) {
              listElem.scrollTop += previousElemTop - listElemTop;
            }

            return options[previousIndex].value;
          });
        }
        case KeyCode.ENTER: {
          const value = options[index].value;
          setOpened(false);
          onChange(toEventObject(name, value));
          onClose();
          return;
        }
      }
    };

    const selectedItemElem =
      listElem.querySelector('li.selected') ?? itemElems[0];
    const initialIndex = [...itemElems].indexOf(selectedItemElem);
    const initialValue = options[initialIndex].value;
    if (initialValue !== prevFocusedValueRef.current) {
      setFocusedValue(initialValue);
      const {
        top: listElemTop,
        bottom: listElemBottom,
      } = listElem.getBoundingClientRect();
      const {
        top: selectedItemElemTop,
        bottom: selectedItemElemBottom,
      } = selectedItemElem.getBoundingClientRect();
      if (selectedItemElemBottom > listElemBottom) {
        listElem.scrollTop += selectedItemElemBottom - listElemBottom;
      }
      if (selectedItemElemTop < listElemTop) {
        listElem.scrollTop += selectedItemElemTop - listElemTop;
      }
    }

    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('keydown', onKeydown);
    };
  }, [opened, options]);

  return (
    <div ref={elemRef} className={classNames('select_cover', className)}>
      <button
        type="button"
        className={classNames(small ? 'lw_btn_select_32' : 'lw_btn_select', {
          on: opened,
        })}
        disabled={disabled}
        onClick={handleClick}
      >
        {label}
      </button>
      <div ref={contextElemRef} className="ly_context">
        <SelectContext.Provider
          value={{
            dynamic: _.isFunction(children),
            setOptions,
            selectedValue: value,
            focusedValue,
            setLabel,
            onSelect: handleSelect,
          }}
        >
          {_.isFunction(children) ? (
            children({
              options,
              setOptions,
              close: () => {
                setOpened(false);
                onClose();
              },
            })
          ) : (
            <ul>{children}</ul>
          )}
        </SelectContext.Provider>
      </div>
    </div>
  );
}
Select.Option = Option;

Option.propTypes = {
  value: PropTypes.any.isRequired,
  onRemove: PropTypes.func,
  removeDimmed: PropTypes.bool,
  children: PropTypes.node.isRequired,
};
function Option({ value, onRemove, removeDimmed = false, children }) {
  const { t } = useTranslation();

  const {
    dynamic,
    setOptions,
    selectedValue,
    focusedValue,
    setLabel,
    onSelect,
  } = useContext(SelectContext);
  useEffect(() => {
    if (dynamic) return;

    setOptions((options) => [...options, { value, label: children }]);
  }, []);

  const label = children ?? value;
  const focused = value === focusedValue;
  const selected = value === selectedValue;
  useEffect(() => {
    if (!selected) return;
    setLabel(label);
  }, [selected]);

  const handleMouseDown = () => onSelect(value);
  const handleMouseDownRemove = () => onRemove(value);

  return (
    <li className={classNames({ focus: focused, selected })}>
      <a href="#" onMouseDown={preventDefault(handleMouseDown)}>
        <span className="text">{label}</span>
        {onRemove && (
          <button
            type="button"
            className={classNames('btn_remove', { inactive: removeDimmed })}
            onMouseDownCapture={preventDefault(
              stopPropagation(handleMouseDownRemove),
            )}
          >
            <i className="blind">{t('button_delete')}</i>
          </button>
        )}
      </a>
    </li>
  );
}
